import { useSecureGraphqlLazyQuery, useSecureGraphqlMutation } from '@psyomics/ui-common';

import {
  DeleteSurveyAttachmentDocument,
  DeleteSurveyAttachmentInput,
  GetSurveyAttachmentDownloadUrlDocument,
  GetSurveyAttachmentDownloadUrlInput,
  GetSurveyAttachmentDownloadUrlQuery,
  GetSurveyResponseAttachmentsDocument,
  GetSurveyResponseAttachmentsQuery,
} from '../generated/survey-responses';

const useDeleteSurveyAttachment = () =>
  useSecureGraphqlMutation<
    { deleteSurveyAttachment: boolean },
    { input: DeleteSurveyAttachmentInput },
    'AttachmentNotFound'
  >(DeleteSurveyAttachmentDocument, ['AttachmentNotFound' as const]);

const useGetSurveyAttachmentDownloadUrl = () =>
  useSecureGraphqlLazyQuery<GetSurveyAttachmentDownloadUrlQuery, { input: GetSurveyAttachmentDownloadUrlInput }>(
    GetSurveyAttachmentDownloadUrlDocument
  );

const useGetSurveyResponseAttachments = () =>
  useSecureGraphqlLazyQuery<GetSurveyResponseAttachmentsQuery, { surveyResponseId: string }>(
    GetSurveyResponseAttachmentsDocument
  );

async function uploadSurveyAttachment({
  patientApiUrl,
  surveyResponseId,
  questionName,
  file,
}: {
  patientApiUrl: string;
  surveyResponseId: string;
  questionName: string;
  file: File;
}): Promise<{
  contentType: string;
  fileName: string;
  id: string;
  parentId: string;
  sizeBytes: number;
}> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(
    `${patientApiUrl}/survey-responses/${surveyResponseId}/questions/${questionName}/attachments`,
    {
      body: formData,
      credentials: 'include',
      method: 'POST',
    }
  );

  if (!response.ok) {
    const body: { message?: string; messageKey?: string } = await Promise.resolve()
      .then(() => response.json())
      .catch(() => ({}));
    throw new Error(body.messageKey || body.message || `Failed to upload survey attachment: ${response.status}`);
  }

  return response.json();
}

export {
  uploadSurveyAttachment,
  useDeleteSurveyAttachment,
  useGetSurveyAttachmentDownloadUrl,
  useGetSurveyResponseAttachments,
};
